import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useLang } from '../../contexts/LangContext';
import { apartmentsApi, type Apartment, type UpdateApartmentData, type Building, type ApartmentGroup, type ApartmentUsageType, type ApartmentMember, type ApartmentFeeCategory } from '../../api/apartmentsApi';
import { BulkListPagination } from '../../components/bulk-fee/BulkListPagination';
import { HISTORY_PAGE_SIZE } from '../../api/paginatedHistory';
import { buildingsApi } from '../../api/buildingsApi';
import { serviceFeeApi } from '../../api/serviceFeeApi';
import { billsApi, Bill } from '../../api/billsApi';
import { feeCategoriesApi } from '../../api/feeCategoriesApi';
import { chatApi } from '../../api/chatApi';
import AddMemberModal from '../../components/modals/AddMemberModal';
import EditMemberModal from '../../components/modals/EditMemberModal';
import EditFeeCategoryModal from '../../components/modals/EditFeeCategoryModal';
import AddFixedServiceFeeModal from '../../components/modals/AddFixedServiceFeeModal';
import AddUsageServiceFeeModal from '../../components/modals/AddUsageServiceFeeModal';
import AddOtherServiceFeeModal from '../../components/modals/AddOtherServiceFeeModal';
import { AddCasualParkingFeeModal } from '../../components/service-fees/AddCasualParkingFeeModal';
import { ServiceFeeInvoiceAmountCell } from '../../components/service-fees/ServiceFeeInvoiceAmountCell';
import { formatDateToDisplay, parseDisplayDateToIso } from '../../utils/apartmentDate';
import { isValidEmailList } from '../../utils/emailList';
import { getRelationshipLabelByCode, getResidentRoleLabel } from '../../utils/residentLookup';
import { MobileAppLinkStatus } from '../../components/residents/MobileAppLinkStatus';
import AdjustServiceFeeModal from '../../components/modals/AdjustServiceFeeModal';
import { BillDetailsModal } from '../../components/modals/BillDetailsModal';
import EmailDetailModal from '../../components/modals/EmailDetailModal';
import ApartmentServiceFeePaymentModal from '../../components/modals/ApartmentServiceFeePaymentModal';
import ApartmentFeeNotificationModal from '../../components/modals/ApartmentFeeNotificationModal';
import ApartmentFeePdfExportModal from '../../components/modals/ApartmentFeePdfExportModal';
import { ViewApartmentFeeNoticeButton } from '../../components/fee-notice/ViewApartmentFeeNoticeButton';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { ChatBubbleLeftIcon } from '@heroicons/react/24/outline';
import { buildCompositeBillServiceLines } from '../../utils/feeCategoryCompositePricing';
import { formatServiceFeeAdjustmentLine } from '../../utils/serviceFeeAdjustmentDisplay';
import {
  buildServiceFeeDisplayGroups,
  filterResidentFeeCategories,
  filterKtdvFeeCategoriesForApartmentUsage,
  collectConfiguredManagementFeeCodes,
  filterManagementFeeCategoriesForApartmentUsage,
  filterApartmentDanhMucPhiCategories,
  buildApartmentFeeCategoryTableRows,
  isCasualParkingFeeCode,
} from '../../utils/feeCategoryCodeGrouping';
import { isMeterBasedFeeCategory } from '../../utils/feeCategoryBillingKind';
import { formatCardIssuanceFeeNotes } from '../../utils/accessCardFee';
import { formatChargingInvoiceDetailLabel } from '../../utils/chargingInvoiceDisplay';
import {
  getFeeCategoryDisplayUnit,
  isApartmentQuantityEditable,
  usesPerInvoiceQuantity,
} from '../../utils/feeQuantity';
import { getCasualParkingInvoiceContent } from '../../utils/casualParkingInvoice';
import {
  compareBillingPeriods,
  sumAllocatableOwed,
  getInvoiceListDisplayAmount,
} from '../../utils/serviceFeeDebt';
import { getQuotaTierShortLabel } from '../../utils/quotaTierLabels';
import toast from 'react-hot-toast';
import { assertCanAddManualFixedParkingInvoice } from '../../utils/manualParkingInvoiceGuard';

// Use ApartmentMember from API instead of local interface

const parseMonthlyParkingFeeForDisplay = (value: unknown): number | null => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') return Number.isFinite(value) ? Math.round(value) : null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.round(parsed) : null;
};

const getParkingInvoiceUnitPriceDisplay = (
  invoice: {
    monthlyVehicleId?: string | null;
    monthlyVehicle?: { monthlyParkingFee?: unknown } | null;
  },
  feeCategory: { basePrice?: number | null; vatPercentage?: number | null },
): { unitPrice: number; vatPercent: number } => {
  if (invoice.monthlyVehicleId) {
    const monthlyFee = parseMonthlyParkingFeeForDisplay(
      invoice.monthlyVehicle?.monthlyParkingFee,
    );
    if (monthlyFee !== null) {
      return { unitPrice: monthlyFee, vatPercent: 0 };
    }
  }
  return {
    unitPrice: feeCategory.basePrice || 0,
    vatPercent: feeCategory.vatPercentage || 0,
  };
};

const getBillServiceNameLines = (feeCategory: {
  name: string;
  extraFee1Name?: string | null;
  extraFee2Name?: string | null;
  extraFee1Percent?: number | null;
  extraFee1Amount?: number | null;
  extraFee1Fixed?: number | null;
  extraFee2Percent?: number | null;
  extraFee2Amount?: number | null;
  extraFee2Fixed?: number | null;
}) => buildCompositeBillServiceLines(feeCategory.name, feeCategory);



const ApartmentDetailsEnhanced: React.FC = () => {
  const { t } = useLang();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [activeTab, setActiveTab] = useState(() => searchParams.get('tab') || 'general');
  const [error, setError] = useState<string | null>(null);
  const [buildings, setBuildings] = useState<Building[]>([]);
  const [apartmentGroups, setApartmentGroups] = useState<ApartmentGroup[]>([]);
  const [usageTypes, setUsageTypes] = useState<ApartmentUsageType[]>([]);
  const [apartment, setApartment] = useState<Apartment | null>(null);
  const [members, setMembers] = useState<ApartmentMember[]>([]);
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [showEditMemberModal, setShowEditMemberModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState<ApartmentMember | null>(null);
  const [feeCategories, setFeeCategories] = useState<ApartmentFeeCategory[]>([]);
  const [showEditFeeCategoryModal, setShowEditFeeCategoryModal] = useState(false);
  const [selectedFeeCategory, setSelectedFeeCategory] = useState<ApartmentFeeCategory | null>(null);
  const [loadingFeeCategories, setLoadingFeeCategories] = useState(false);
  
  // Service fee modals state
  const [showAddFixedFeeModal, setShowAddFixedFeeModal] = useState(false);
  const [showAddUsageFeeModal, setShowAddUsageFeeModal] = useState(false);
  const [showAddOtherInvoiceModal, setShowAddOtherInvoiceModal] = useState(false);
  const [showCasualParkingModal, setShowCasualParkingModal] = useState(false);
  const [showAdjustServiceFeeModal, setShowAdjustServiceFeeModal] = useState(false);
  const [selectedServiceFeeType, setSelectedServiceFeeType] = useState<string>('');
  const [selectedFeeForModal, setSelectedFeeForModal] = useState<ApartmentFeeCategory | null>(null);
  const [selectedInvoiceForAdjustment, setSelectedInvoiceForAdjustment] = useState<any>(null);
  
  // Service fee invoices data
  const [serviceFeeInvoices, setServiceFeeInvoices] = useState<any[]>([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [buildingSettings, setBuildingSettings] = useState<any>(null);
  const [paymentHistory, setPaymentHistory] = useState<Bill[]>([]);
  const [loadingPaymentHistory, setLoadingPaymentHistory] = useState(false);
  
  // Bill detail modal state
  const [showBillDetailsModal, setShowBillDetailsModal] = useState(false);
  const [selectedBillId, setSelectedBillId] = useState('');
  
  // Email detail modal state
  const [showEmailDetailModal, setShowEmailDetailModal] = useState(false);
  const [selectedEmailNotification, setSelectedEmailNotification] = useState<any>(null);

  // Service fee action modals
  const [showServiceFeePaymentModal, setShowServiceFeePaymentModal] = useState(false);
  const [showServiceFeePdfModal, setShowServiceFeePdfModal] = useState(false);
  const [showServiceFeeNotificationModal, setShowServiceFeeNotificationModal] = useState(false);
  
  // Payment history filters - default to 6 months from current day
  const getDefaultDates = () => {
    const today = new Date();
    const sixMonthsAgo = new Date();
    sixMonthsAgo.setMonth(today.getMonth() - 6);
    
    return {
      fromDate: sixMonthsAgo,
      toDate: today
    };
  };

  const defaultDates = getDefaultDates();
  const [paymentFromDate, setPaymentFromDate] = useState<Date>(defaultDates.fromDate);
  const [paymentToDate, setPaymentToDate] = useState<Date>(defaultDates.toDate);

  const [formData, setFormData] = useState<UpdateApartmentData>({
    id: '',
    code: '',
    name: '',
    occupancyStatus: undefined,
    handoverDate: '',
    peCode: '',
    populationCount: undefined,
    ownerName: '',
    ownerPhone: '',
    ownerEmail: '',
    invoiceEmail: '',
    address: '',
    area: undefined,
    taxCode: '',
    notes: '',
    hasVatInvoice: false,
    buildingId: '',
    groupId: '',
    usageTypeId: '',
    registeredQuotaCount: undefined,
  });

  const handleModalClose = React.useCallback(() => {
    setShowAddMemberModal(false);
  }, []);

  useEffect(() => {
    if (id) {
      loadApartment();
      loadBuildings();
      loadMembers();
      loadFeeCategories();
      loadServiceFeeInvoices();
      loadPaymentHistory(defaultDates.fromDate, defaultDates.toDate);
    }
  }, [id]);

  // Load building settings after apartment is loaded
  useEffect(() => {
    if (apartment?.buildingId) {
      loadBuildingSettings();
    }
  }, [apartment?.buildingId]);

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab) {
      setActiveTab(tab);
    }
  }, [searchParams]);

  const loadMembers = async () => {
    if (!id) return;
    
    try {
      const data = await apartmentsApi.getApartmentMembers(id);
      setMembers(data);
    } catch (err) {
      console.error('Failed to load members:', err);
    }
  };

  const loadApartment = async () => {
    if (!id) return;
    
    try {
      setLoadingData(true);
      const data = await apartmentsApi.getApartmentById(id);
      setApartment(data);
      setFormData({
        id: data.id,
        code: data.code,
        name: data.name || '',
        occupancyStatus: data.occupancyStatus,
        handoverDate: formatDateToDisplay(data.handoverDate),
        peCode: data.peCode || '',
        populationCount: data.populationCount,
        ownerName: data.ownerName,
        ownerPhone: data.ownerPhone || '',
        ownerEmail: data.ownerEmail || '',
        invoiceEmail: data.invoiceEmail || '',
        address: data.address || '',
        area: data.area,
        taxCode: data.taxCode || '',
        notes: data.notes || '',
        hasVatInvoice: data.hasVatInvoice || false,
        buildingId: data.buildingId,
        groupId: data.groupId || '',
        usageTypeId: data.usageTypeId || '',
        registeredQuotaCount: data.registeredQuotaCount,
      });
      loadUsageTypes(data.buildingId);
      loadApartmentGroups(data.buildingId);
    } catch (err) {
      setError(t('errorLoadingApartments'));
    } finally {
      setLoadingData(false);
    }
  };

  const loadBuildings = async () => {
    try {
      const data = await apartmentsApi.getBuildings();
      setBuildings(data);
    } catch (err) {
      setError(t('failedToLoad'));
    }
  };

  const loadApartmentGroups = async (buildingId?: string) => {
    if (!buildingId) {
      setApartmentGroups([]);
      return;
    }
    try {
      const data = await apartmentsApi.getApartmentGroups(buildingId);
      setApartmentGroups(data);
    } catch (err) {
      console.error('Failed to load apartment groups:', err);
      setApartmentGroups([]);
    }
  };

  const loadUsageTypes = async (buildingId?: string) => {
    if (!buildingId) {
      setUsageTypes([]);
      return;
    }
    try {
      const data = await apartmentsApi.getApartmentUsageTypes(buildingId);
      setUsageTypes(data);
    } catch (err) {
      console.error('Failed to load usage types:', err);
    }
  };

  const buildingManagementFeeCodes = useMemo(
    () => collectConfiguredManagementFeeCodes(usageTypes),
    [usageTypes],
  );

  const apartmentUsageTypeForFees = useMemo(() => {
    if (!apartment?.usageTypeId) {
      return apartment?.usageType ?? null;
    }
    const fromList = usageTypes.find((type) => type.id === apartment.usageTypeId);
    return fromList ?? apartment?.usageType ?? null;
  }, [apartment?.usageType, apartment?.usageTypeId, usageTypes]);

  const loadFeeCategories = async () => {
    if (!id) return;
    
    try {
      setLoadingFeeCategories(true);
      console.log('Loading fee categories for apartment:', id);
      const data = await apartmentsApi.getApartmentFeeCategories(id);
      console.log('Fee categories loaded:', data);
      setFeeCategories(data);
    } catch (err) {
      console.error('Failed to load fee categories:', err);
    } finally {
      setLoadingFeeCategories(false);
    }
  };

  const loadServiceFeeInvoices = async () => {
    if (!id) return;
    
    try {
      setLoadingInvoices(true);
      // Only load unpaid service fees (exclude PAID status)
      const response = await serviceFeeApi.getServiceFeeInvoices({ 
        apartmentId: id,
        status: 'PENDING,OVERDUE,PARTIAL,CANCELLED,ADJUSTED' // Exclude PAID status
      });
      setServiceFeeInvoices(response || []);
    } catch (err) {
      console.error('Failed to load service fee invoices:', err);
      setServiceFeeInvoices([]);
    } finally {
      setLoadingInvoices(false);
    }
  };

  const loadBuildingSettings = async () => {
    if (!apartment?.buildingId) return;
    
    try {
      const buildingData = await buildingsApi.getBuilding(apartment.buildingId);
      setBuildingSettings(buildingData);
    } catch (err) {
      console.error('Failed to load building settings:', err);
    }
  };

  const loadPaymentHistory = async (fromDate?: Date, toDate?: Date) => {
    if (!id) return;
    
    setLoadingPaymentHistory(true);
    try {
      const query: any = { apartmentId: id };
      if (fromDate) query.fromDate = fromDate.toISOString().split('T')[0];
      if (toDate) query.toDate = toDate.toISOString().split('T')[0];
      
      console.log('Loading payment history with query:', query);
      const bills = await billsApi.getBills(query);
      setPaymentHistory(bills);
    } catch (error) {
      console.error('Error loading payment history:', error);
    } finally {
      setLoadingPaymentHistory(false);
    }
  };

  const handleSaveFeeCategory = async (feeCategoryId: string, quantity: number, notes: string) => {
    if (!id) return;
    
    try {
      await apartmentsApi.updateApartmentFeeCategory(id, feeCategoryId, { quantity, notes });
      // Reload fee categories to show updated data
      await loadFeeCategories();
    } catch (error) {
      console.error('Error updating fee category:', error);
      throw error;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!formData.code?.trim()) {
      setError(t('pleaseEnterApartmentCode'));
      return;
    }
    if (!formData.ownerName?.trim()) {
      setError(t('pleaseEnterOwnerName'));
      return;
    }
    if (!formData.buildingId) {
      setError(t('pleaseSelectBuilding'));
      return;
    }

    try {
      setLoading(true);
      const submitData = {
        ...formData,
        groupId: formData.groupId || undefined,
        area: formData.area || undefined
      };
      await apartmentsApi.updateApartment(submitData);
      alert(t('apartmentUpdatedSuccess'));
      // Don't reload apartment data to avoid input focus loss
      // The form data is already up to date
    } catch (err) {
      setError(t('errorUpdatingApartment'));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked 
             : name === 'area' ? (value ? parseFloat(value) : undefined) 
             : value
    }));
  };

  const buildingOptions = useMemo(() => 
    buildings.map((building) => (
      <option key={building.id} value={building.id}>
        {building.name}
      </option>
    )), [buildings]
  );

  // Members Tab Component
  const MembersTab = () => {
    const formatDate = (dateString?: string) => {
      if (!dateString) return '-';
      return formatDateToDisplay(dateString) || dateString.split('T')[0];
    };

    return (
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="px-4 py-3 border-b border-gray-200 flex justify-between items-center gap-4">
          <div>
            <h3 className="text-lg font-medium text-gray-900">{t('memberList')}</h3>
            <p className="text-sm text-gray-500 mt-1">{t('membersTabReadOnlyHint')}</p>
          </div>
          <Link
            to="/apartments/residents"
            className="inline-flex items-center px-3 py-2 border border-blue-300 text-sm font-medium rounded-md text-blue-700 bg-white hover:bg-blue-50"
          >
            {t('manageResidents')}
          </Link>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">#</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">{t('residentName')}</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">{t('dateOfBirth')}</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">{t('relationshipRole')}</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">{t('residentRole')}</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">{t('email')}</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">{t('phone')}</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">{t('mobileAppAccount')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {members.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                    {t('noResidents')}
                  </td>
                </tr>
              ) : (
                members.map((member, index) => (
                  <tr key={member.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm">{index + 1}</td>
                    <td className="px-4 py-3 text-sm font-medium">{member.fullName}</td>
                    <td className="px-4 py-3 text-sm">{formatDate(member.dateOfBirth)}</td>
                    <td className="px-4 py-3 text-sm">{getRelationshipLabelByCode(member.relationshipCode, t) || '-'}</td>
                    <td className="px-4 py-3 text-sm">{getResidentRoleLabel(member.role, t)}</td>
                    <td className="px-4 py-3 text-sm">{member.email || '-'}</td>
                    <td className="px-4 py-3 text-sm">{member.phone || '-'}</td>
                    <td className="px-4 py-3 text-sm">
                      <MobileAppLinkStatus member={member} t={t} />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // Service Fees Tab Component
  const ServiceFeesTab = () => {
    const formatPrice = (price: number) => {
      return new Intl.NumberFormat('vi-VN', { maximumFractionDigits: 0 }).format(Math.round(price));
    };

    const formatUsage = (value: number | null | undefined) => {
      if (value == null || Number.isNaN(Number(value))) return '--';
      const rounded = Math.round(Number(value) * 1000) / 1000;
      return new Intl.NumberFormat('vi-VN', { maximumFractionDigits: 3 }).format(rounded);
    };

    const getUnitDisplayForFee = (fee: any) => {
      if (!fee?.feeCategory) return '';
      
      const { feeCategory } = fee;
      
      if (feeCategory.pricingType === 'SINGLE_TIER') {
        return feeCategory.isMonthlyFixed ? 'căn' : 'm²';
      } else if (feeCategory.pricingType === 'TIERED') {
        if (feeCategory.feeTiers && feeCategory.feeTiers.length > 0) {
          return feeCategory.feeTiers[0].unit || '';
        }
        return '';
      } else if (feeCategory.pricingType === 'QUOTA_BASED') {
        // For quota-based, show the unit based on the fee category name
        if (feeCategory.name.toLowerCase().includes('nước')) return 'm³';
        if (feeCategory.name.toLowerCase().includes('điện')) return 'kWh';
        if (feeCategory.name.toLowerCase().includes('xe')) return 'xe';
        return 'đơn vị';
      }
      return '';
    };

    // Group fee categories by type for display (DV resident fees only)
    const residentActiveFeeCategories = filterResidentFeeCategories(
      filterManagementFeeCategoriesForApartmentUsage(
        filterKtdvFeeCategoriesForApartmentUsage(
          feeCategories,
          apartmentUsageTypeForFees?.code,
        ),
        apartmentUsageTypeForFees,
        buildingManagementFeeCodes,
      ),
    );
    const fixedFees = residentActiveFeeCategories.filter(
      (fc) =>
        !isMeterBasedFeeCategory(fc.feeCategory) &&
        !isCasualParkingFeeCode(fc.feeCategory.feeCode),
    );
    const usageFees = residentActiveFeeCategories.filter((fc) =>
      isMeterBasedFeeCategory(fc.feeCategory),
    );

    const getServiceFeeTitle = (feeCategory: ApartmentFeeCategory) =>
      getBillServiceNameLines(feeCategory.feeCategory).join(' / ');

    const fixedFeeGroups = buildServiceFeeDisplayGroups(
      fixedFees,
      feeCategories,
      getServiceFeeTitle,
    );
    const usageFeeGroups = buildServiceFeeDisplayGroups(
      usageFees,
      feeCategories,
      getServiceFeeTitle,
    );

    const renderServiceFeeGroup = (
      group: ReturnType<typeof buildServiceFeeDisplayGroups<ApartmentFeeCategory>>[number],
      isUsageBased: boolean,
    ) => (
      <div key={group.key} className="mb-8">
        {group.headerTitle && (
          <h2 className="text-xl font-semibold text-blue-700 mb-4 border-b border-blue-200 pb-2">
            {group.headerTitle}
          </h2>
        )}
        {group.items.map((feeCategory) => (
          <div key={feeCategory.feeCategoryId}>
            {renderServiceFeeSection(
              getServiceFeeTitle(feeCategory),
              [feeCategory],
              isUsageBased,
            )}
          </div>
        ))}
      </div>
    );

    const renderServiceFeeSection = (title: string, fees: ApartmentFeeCategory[], isUsageBased = false) => {
      // Get all invoices for this fee category section
      const sectionInvoices = fees
        .reduce((acc: any[], fee) => {
          const invoices = serviceFeeInvoices.filter(
            (invoice) =>
              invoice.feeCategoryId === fee.feeCategoryId &&
              invoice.invoiceType !== 'CASUAL_PARKING',
          );
          return [...acc, ...invoices.map((invoice) => ({ ...invoice, fee }))];
        }, [] as any[])
        .sort((a, b) => compareBillingPeriods(a.billingPeriod, b.billingPeriod));

      const isParkingSection =
        sectionInvoices.some((invoice) => invoice.monthlyVehicleId) ||
        fees.some((fee) => {
          const code = fee.feeCategory?.feeCode?.toUpperCase() ?? '';
          return (
            code === 'DV002' ||
            code.startsWith('DV002') ||
            code.startsWith('XE') ||
            fee.feeCategory?.name?.toLowerCase().includes('gửi xe')
          );
        });

      return (
        <div className="mb-8">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-blue-600 border-l-4 border-blue-600 pl-3">
              {title}
            </h3>
            <button 
              onClick={() => openAddFixedServiceFeeModal(fees[0] || null, title, isUsageBased)}
              className="text-red-500 hover:text-red-700 border border-red-500 hover:border-red-700 px-3 py-1 rounded text-sm"
            >
              Thêm hóa đơn
            </button>
          </div>
          
          {sectionInvoices.length === 0 ? (
            // Show individual empty message for this section
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
              <p className="text-red-500">Không có hóa đơn tồn hoặc chưa đến ngày chốt phí dịch vụ.</p>
              <p className="text-sm text-gray-600 mt-2">
                {isUsageBased ? 
                  'Với phí theo mức sử dụng, bạn cần thêm hóa đơn thủ công.' : 
                  'Với phí cố định, hệ thống sẽ tự động tạo hóa đơn hàng tháng.'
                }
              </p>
            </div>
          ) : (
            <div className="bg-blue-50 border border-blue-200 rounded-lg overflow-hidden">
              <table className="min-w-full">
                <thead className="bg-blue-100">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">#</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Kỳ hóa đơn</th>
                    {isUsageBased ? (
                      <>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Mã đồng hồ</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Đầu kỳ</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Cuối kỳ</th>
                      </>
                    ) : null}
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Số lượng</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Số tiền</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Chi tiết</th>
                    {isParkingSection ? (
                      <>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Ngày hết hạn cũ</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Ngày hết hạn mới</th>
                      </>
                    ) : null}
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Hạn thanh toán</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Ghi chú</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700"></th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {sectionInvoices.map((invoiceWithFee: any, index: number) => (
                    <tr key={`${invoiceWithFee.feeCategoryId}-${index}`} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm">{index + 1}</td>
                      <td className="px-4 py-3 text-sm">{invoiceWithFee.billingPeriod}</td>
                      {isUsageBased ? (
                        <>
                          <td className="px-4 py-3 text-sm">
                            {invoiceWithFee.utilityMeter?.meterCode || '--'}
                            {(invoiceWithFee.utilityMeter?.quotaTierLevel ??
                              invoiceWithFee.utilityMeter?.quotaTierStart) ? (
                              <div className="text-xs text-gray-500">
                                {getQuotaTierShortLabel(
                                  invoiceWithFee.utilityMeter.quotaTierLevel ??
                                    invoiceWithFee.utilityMeter.quotaTierStart,
                                )}
                              </div>
                            ) : null}
                          </td>
                          <td className="px-4 py-3 text-sm">{formatUsage(invoiceWithFee.startReading)}</td>
                          <td className="px-4 py-3 text-sm">{formatUsage(invoiceWithFee.endReading)}</td>
                        </>
                      ) : null}
                      <td className="px-4 py-3 text-sm">
                        {(() => {
                          const qty = invoiceWithFee.usage ?? invoiceWithFee.fee?.quantity;
                          if (qty == null) return '--';
                          const unit = getFeeCategoryDisplayUnit(invoiceWithFee.fee?.feeCategory || {});
                          return `${formatUsage(qty)} ${unit}`.trim();
                        })()}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <ServiceFeeInvoiceAmountCell
                          invoice={invoiceWithFee}
                          displayAmount={getInvoiceListDisplayAmount(invoiceWithFee)}
                        >
                          {invoiceWithFee.status === 'ADJUSTED' && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                              Đã điều chỉnh
                            </span>
                          )}
                        </ServiceFeeInvoiceAmountCell>
                        {invoiceWithFee.adjustments && invoiceWithFee.adjustments.length > 0 && (
                          <div className="text-xs text-orange-600 mt-1">
                            {invoiceWithFee.adjustments.map((adj: any, adjIndex: number) => {
                              const line = formatServiceFeeAdjustmentLine(adj);
                              if (!line) return null;
                              return (
                                <div key={adjIndex}>
                                  Điều chỉnh: {line}
                                </div>
                              );
                            })}
                          </div>
                        )}
                        {/* Show quota-based pricing breakdown if available */}
                        {invoiceWithFee.fee?.feeCategory?.pricingType === 'QUOTA_BASED' && invoiceWithFee.usage && (
                          <div className="text-xs text-blue-600 mt-1">
                            <div>Sử dụng: {invoiceWithFee.usage} {getUnitDisplayForFee(invoiceWithFee.fee)}</div>
                            <div>Tính theo định mức</div>
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {/* Show pricing details for quota-based fees */}
                        {invoiceWithFee.fee?.feeCategory?.pricingType === 'QUOTA_BASED' && (
                          <div className="text-xs text-gray-600">
                            <div>Định mức: {(invoiceWithFee.fee.quotaLimit ?? invoiceWithFee.fee.feeCategory.quotaLimit ?? 0)}</div>
                            <div>f1: {formatPrice(invoiceWithFee.fee.feeCategory.quotaPrice || 0)}</div>
                            <div>f2: {formatPrice(invoiceWithFee.fee.feeCategory.overQuotaPrice1 || 0)}</div>
                            <div>f3: {formatPrice(invoiceWithFee.fee.feeCategory.overQuotaPrice2 || 0)}</div>
                          </div>
                        )}
                        {/* Show tiered pricing details */}
                        {invoiceWithFee.fee?.feeCategory?.pricingType === 'TIERED' && (
                          <div className="text-xs text-gray-600">
                            <div>Bậc thang</div>
                            <div>{invoiceWithFee.fee.feeCategory.feeTiers?.length || 0} mức</div>
                            {(invoiceWithFee.utilityMeter?.quotaTierLevel ??
                              invoiceWithFee.utilityMeter?.quotaTierStart) ? (
                              <div>
                                Đồng hồ:{' '}
                                {getQuotaTierShortLabel(
                                  invoiceWithFee.utilityMeter.quotaTierLevel ??
                                    invoiceWithFee.utilityMeter.quotaTierStart,
                                )}
                              </div>
                            ) : null}
                          </div>
                        )}
                        {/* Show single tier details */}
                        {invoiceWithFee.fee?.feeCategory?.pricingType === 'SINGLE_TIER' && (() => {
                          const { unitPrice, vatPercent } = getParkingInvoiceUnitPriceDisplay(
                            invoiceWithFee,
                            invoiceWithFee.fee.feeCategory,
                          );
                          return (
                            <div className="text-xs text-gray-600">
                              <div>Đơn giá: {formatPrice(unitPrice)}</div>
                              <div>VAT: {vatPercent}%</div>
                            </div>
                          );
                        })()}
                      </td>
                      {isParkingSection ? (
                        <>
                          <td className="px-4 py-3 text-sm">
                            {formatDateToDisplay(invoiceWithFee.parkingExpiryBefore) || '--'}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            {formatDateToDisplay(invoiceWithFee.parkingExpiryAfter) || '--'}
                          </td>
                        </>
                      ) : null}
                      <td className="px-4 py-3 text-sm text-red-500">
                        {new Date(invoiceWithFee.dueDate).toLocaleDateString('vi-VN')}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {formatCardIssuanceFeeNotes(invoiceWithFee) ||
                          formatChargingInvoiceDetailLabel({
                            notes: invoiceWithFee.notes,
                            isAutoGenerated: invoiceWithFee.isAutoGenerated,
                            createdAt: invoiceWithFee.createdAt,
                            feeCode: invoiceWithFee.fee?.feeCategory?.feeCode,
                            categoryName: invoiceWithFee.fee?.feeCategory?.name,
                            usage: invoiceWithFee.usage,
                          }) ||
                          invoiceWithFee.notes ||
                          (invoiceWithFee.isAutoGenerated
                            ? 'Chốt phí tự động'
                            : 'Chốt phí thủ công')}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div className="flex space-x-2">
                          <button 
                            onClick={() => handleDeleteInvoice(invoiceWithFee.id)}
                            className="text-red-500 hover:text-red-700"
                            title="Xóa"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                          <button 
                            onClick={() => handleAdjustInvoice(invoiceWithFee.id, invoiceWithFee.fee)}
                            className="text-blue-500 hover:text-blue-700"
                            title="Điều chỉnh"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      );
    };

    const renderCasualParkingSection = () => {
      const casualInvoices = serviceFeeInvoices
        .filter((invoice) => invoice.invoiceType === 'CASUAL_PARKING')
        .sort((a, b) => compareBillingPeriods(a.billingPeriod, b.billingPeriod));
      const hasCasualParkingCategory = feeCategories.some(
        (fc) =>
          fc.feeCategory.feeCode === 'T-VL001' &&
          fc.feeCategory.isActive !== false,
      );

      if (!hasCasualParkingCategory && casualInvoices.length === 0) {
        return null;
      }

      return (
        <div className="mb-8">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-emerald-700 border-l-4 border-emerald-600 pl-3">
              Thu tiền xe vãng lai
            </h3>
            <button
              onClick={() => setShowCasualParkingModal(true)}
              className="text-emerald-700 hover:text-emerald-900 border border-emerald-600 hover:border-emerald-800 px-3 py-1 rounded text-sm"
            >
              Thêm hóa đơn
            </button>
          </div>
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg overflow-hidden">
            <table className="min-w-full">
              <thead className="bg-emerald-100">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">#</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Kỳ hóa đơn</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Máy giữ xe</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Thực thu</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Khấu trừ / Voucher</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Nội dung</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Ghi chú</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700"></th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {casualInvoices.map((invoice, index) => {
                  const variances = invoice.casualParkingVariances ?? [];
                  const deduction = variances
                    .filter((v: { varianceType: string }) => v.varianceType === 'DEDUCTION')
                    .reduce((s: number, v: { amount: number }) => s + (v.amount ?? 0), 0);
                  const voucher = variances
                    .filter((v: { varianceType: string }) => v.varianceType === 'VOUCHER')
                    .reduce((s: number, v: { amount: number }) => s + (v.amount ?? 0), 0);
                  return (
                  <tr key={invoice.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm">{index + 1}</td>
                    <td className="px-4 py-3 text-sm">{invoice.billingPeriod}</td>
                    <td className="px-4 py-3 text-sm">
                      {invoice.machineAmount != null ? formatPrice(invoice.machineAmount) : '—'}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <ServiceFeeInvoiceAmountCell
                        invoice={invoice}
                        displayAmount={getInvoiceListDisplayAmount(invoice)}
                      />
                    </td>
                    <td className="px-4 py-3 text-sm text-xs">
                      {deduction > 0 ? <div>Khấu trừ: {formatPrice(deduction)}</div> : null}
                      {voucher > 0 ? <div>Voucher: {formatPrice(voucher)}</div> : null}
                      {deduction === 0 && voucher === 0 ? '—' : null}
                      {variances.length > 0 ? (
                        <div className="text-gray-500 mt-1">
                          {variances.map((v: { counterpartyName: string; varianceType: string; amount: number }) => (
                            <div key={`${v.counterpartyName}-${v.varianceType}-${v.amount}`}>
                              {v.varianceType === 'VOUCHER' ? 'V' : 'K'}: {v.counterpartyName} ({formatPrice(v.amount)})
                            </div>
                          ))}
                        </div>
                      ) : null}
                    </td>
                    <td className="px-4 py-3 text-sm">{getCasualParkingInvoiceContent(invoice)}</td>
                    <td className="px-4 py-3 text-sm">{invoice.notes || '—'}</td>
                    <td className="px-4 py-3 text-sm">
                      {invoice.status !== 'PAID' && (
                        <button
                          type="button"
                          onClick={() => handleDeleteOtherInvoice(invoice.id)}
                          className="text-red-500 hover:text-red-700"
                          title="Xóa"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      )}
                    </td>
                  </tr>
                  );
                })}
                {casualInvoices.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-4 py-6 text-center text-sm text-gray-500">
                      Chưa có hóa đơn xe vãng lai
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      );
    };

    const renderOtherInvoicesSection = () => (
      <div className="mb-8">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-blue-600 border-l-4 border-blue-600 pl-3">
            Hóa đơn khác
          </h3>
          <button 
            onClick={() => setShowAddOtherInvoiceModal(true)}
            className="text-red-500 hover:text-red-700 border border-red-500 hover:border-red-700 px-3 py-1 rounded text-sm"
          >
            Thêm hóa đơn
          </button>
        </div>
        
        <div className="bg-blue-50 border border-blue-200 rounded-lg overflow-hidden">
          <table className="min-w-full">
            <thead className="bg-blue-100">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">#</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Kỳ hóa đơn</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Số tiền</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Nội dung</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Chi tiết</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Hạn thanh toán</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700"></th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {serviceFeeInvoices
                .filter((invoice) => invoice.invoiceType === 'OTHER')
                .sort((a, b) => compareBillingPeriods(a.billingPeriod, b.billingPeriod))
                .map((invoice, index) => (
                  <tr key={invoice.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm">{index + 1}</td>
                    <td className="px-4 py-3 text-sm">{invoice.billingPeriod}</td>
                    <td className="px-4 py-3 text-sm">
                      <ServiceFeeInvoiceAmountCell
                        invoice={invoice}
                        displayAmount={getInvoiceListDisplayAmount(invoice)}
                      >
                        {invoice.status === 'ADJUSTED' && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                            Đã điều chỉnh
                          </span>
                        )}
                      </ServiceFeeInvoiceAmountCell>
                      {invoice.adjustments && invoice.adjustments.length > 0 && (
                        <div className="text-xs text-orange-600 mt-1">
                          {invoice.adjustments.map((adj: any, adjIndex: number) => {
                            const line = formatServiceFeeAdjustmentLine(adj);
                            if (!line) return null;
                            return (
                              <div key={adjIndex}>
                                Điều chỉnh: {line}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm">{invoice.notes || '--'}</td>
                    <td className="px-4 py-3 text-sm">
                      <div className="text-xs text-gray-600">
                        <div>Hóa đơn khác</div>
                        <div>Thủ công</div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-red-500">
                      {new Date(invoice.dueDate).toLocaleDateString('vi-VN')}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <div className="flex space-x-2">
                        <button 
                          onClick={() => handleDeleteOtherInvoice(invoice.id)}
                          className="text-red-500 hover:text-red-700"
                          title="Xóa"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                        <button 
                          onClick={() => handleAdjustOtherInvoice(invoice)}
                          className="text-blue-500 hover:text-blue-700"
                          title="Điều chỉnh"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              {serviceFeeInvoices.filter(invoice => invoice.invoiceType === 'OTHER').length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center">
                    <p className="text-red-500">Không có hóa đơn tồn.</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    );

    if (loadingFeeCategories) {
      return (
        <div className="bg-white rounded-lg border border-gray-200 p-8">
          <div className="flex justify-center items-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-2 text-gray-600">Đang tải phí dịch vụ...</span>
          </div>
        </div>
      );
    }

    // Always show sections for active fee categories, even if they have no invoices

    return (
      <div className="space-y-6">
        {renderCasualParkingSection()}
        {renderOtherInvoicesSection()}

        {fixedFeeGroups.length > 0 && fixedFeeGroups.map((group) => renderServiceFeeGroup(group, false))}

        {usageFeeGroups.length > 0 && usageFeeGroups.map((group) => renderServiceFeeGroup(group, true))}

        <div className="bg-yellow-50 border border-yellow-200 rounded-lg px-4 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <span className="font-semibold text-gray-800">Tổng phí phải thu</span>
            <span className="text-xl font-bold text-red-600">
              {formatPrice(sumAllocatableOwed(serviceFeeInvoices))} đ
            </span>
          </div>
          <p className="text-sm text-gray-600 mt-2">
            Tổng số tiền sau thuế của các hóa đơn chưa thanh toán (khớp với Báo cáo phí phải thu).
          </p>
        </div>

        {/* Action buttons */}
        <div className="flex space-x-3 pt-6 border-t">
          <button
            type="button"
            onClick={() => setShowServiceFeePaymentModal(true)}
            className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded"
          >
            Thanh toán phí
          </button>
          {id ? (
            <ViewApartmentFeeNoticeButton
              apartmentId={id}
              apartmentCode={apartment?.code}
              disabled={serviceFeeInvoices.length === 0}
            />
          ) : null}
          <button
            type="button"
            onClick={() => setShowServiceFeePdfModal(true)}
            className="bg-yellow-500 hover:bg-yellow-600 text-white px-4 py-2 rounded"
          >
            Xuất file PDF
          </button>
          <button
            type="button"
            onClick={() => setShowServiceFeeNotificationModal(true)}
            className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded"
          >
            Gửi thông báo phí
          </button>
        </div>
      </div>
    );
  };

  const openAddFixedServiceFeeModal = async (
    fee: ApartmentFeeCategory | null,
    feeTypeTitle: string,
    isUsageBased: boolean,
  ) => {
    if (isUsageBased) {
      setSelectedServiceFeeType(feeTypeTitle);
      setSelectedFeeForModal(fee);
      setShowAddUsageFeeModal(true);
      return;
    }
    if (fee && id && apartment?.buildingId) {
      try {
        await assertCanAddManualFixedParkingInvoice({
          apartmentId: id,
          buildingId: apartment.buildingId,
          feeCategoryId: fee.feeCategoryId,
          feeCode: fee.feeCategory?.feeCode,
          apartmentCode: apartment.code,
        });
      } catch (error: unknown) {
        const message =
          error instanceof Error ? error.message : 'Không thể thêm hóa đơn phí gửi xe thủ công';
        toast.error(message);
        return;
      }
    }
    setSelectedServiceFeeType(feeTypeTitle);
    setSelectedFeeForModal(fee);
    setShowAddFixedFeeModal(true);
  };

  // Service fee management functions
  const handleAddServiceFee = async (data: any) => {
    try {
      if (data.type === 'FIXED') {
        await serviceFeeApi.createFixedServiceFee({
          apartmentId: data.apartmentId,
          feeCategoryId: data.feeCategoryId,
          billingPeriod: data.billingPeriod,
          quantity: data.quantity,
          accessCardId: data.accessCardId,
          contractId: data.contractId,
          fromDate: data.fromDate,
          dueDate: data.dueDate,
        });
        
        // For fixed fees, we need to reload apartment fee categories because
        // the backend might create a new ApartmentFeeCategory with quantity 1
        await loadFeeCategories();
      } else if (data.type === 'USAGE_BASED') {
        // Check if this is a quota-based fee category
        const feeCategory = feeCategories.find((fc) => fc.feeCategoryId === data.feeCategoryId);
        
        if (feeCategory?.feeCategory.pricingType === 'QUOTA_BASED') {
          // For quota-based fees, we need to calculate the amount using the quota pricing service
          const usage = (data.endReading || 0) - (data.startReading || 0);
          
          // Call the backend to calculate quota-based fee
          const calculationResult = await feeCategoriesApi.calculateQuotaBasedFee(
            feeCategory.feeCategoryId,
            usage,
            'RESIDENT', // This should be the actual user role
            'RESIDENT',  // This should be the actual user role
            id || undefined
          );
          
          // Use the calculated amount
          data.amount = calculationResult.totalAmount;
        }
        
        await serviceFeeApi.createUsageServiceFee({
          apartmentId: data.apartmentId,
          feeCategoryId: data.feeCategoryId,
          billingPeriod: data.billingPeriod,
          startReading: data.startReading,
          endReading: data.endReading,
          amount: data.amount,
          utilityMeterId: data.utilityMeterId,
          fromDate: data.fromDate,
          dueDate: data.dueDate,
        });
      } else if (data.type === 'OTHER') {
        await serviceFeeApi.createOtherServiceFee({
          apartmentId: data.apartmentId,
          billingPeriod: data.billingPeriod,
          amount: data.amount,
          notes: data.notes,
          fromDate: data.fromDate,
          dueDate: data.dueDate,
          applyToAllApartments: data.applyToAllApartments || false,
        });
      }
      
      // Reload service fee invoices to show the new data
      await loadServiceFeeInvoices();
      
      alert('Thêm hóa đơn thành công!');
    } catch (error: any) {
      console.error('Error adding service fee:', error);
      throw new Error(error.response?.data?.message || 'Có lỗi xảy ra khi thêm hóa đơn');
    }
  };

  const handleDeleteInvoice = async (invoiceId: string) => {
    if (window.confirm('Bạn có chắc chắn muốn xóa hóa đơn này?')) {
      try {
        const invoice = serviceFeeInvoices.find(
          (row) => row.id === invoiceId && row.invoiceType !== 'CASUAL_PARKING',
        );
        if (!invoice) {
          alert('Không tìm thấy hóa đơn');
          return;
        }
        const result = await serviceFeeApi.deleteServiceFeeInvoice(invoice.id);
        await loadServiceFeeInvoices();
        if (result?.action === 'repaired' && result.message) {
          alert(result.message);
        } else {
          alert('Xóa hóa đơn thành công!');
        }
      } catch (error) {
        console.error('Error deleting invoice:', error);
        alert('Có lỗi xảy ra khi xóa hóa đơn');
      }
    }
  };

  const handleAdjustInvoice = (invoiceId: string, fee: ApartmentFeeCategory) => {
    const invoice = serviceFeeInvoices.find((row) => row.id === invoiceId);
    if (!invoice) {
      return;
    }
    setSelectedInvoiceForAdjustment({
      ...invoice,
      serviceName: getBillServiceNameLines(fee.feeCategory).join(' / '),
      billingPeriod: invoice.billingPeriod,
      currentAmount: getInvoiceListDisplayAmount(invoice),
    });
    setShowAdjustServiceFeeModal(true);
  };

  const handleDeleteOtherInvoice = async (invoiceId: string) => {
    if (window.confirm('Bạn có chắc chắn muốn xóa hóa đơn này?')) {
      try {
        const result = await serviceFeeApi.deleteServiceFeeInvoice(invoiceId);
        await loadServiceFeeInvoices();
        if (result?.action === 'repaired' && result.message) {
          alert(result.message);
        }
      } catch (error) {
        console.error('Error deleting other invoice:', error);
        alert('Có lỗi xảy ra khi xóa hóa đơn');
      }
    }
  };

  const handleAdjustOtherInvoice = (invoice: any) => {
    setSelectedInvoiceForAdjustment({
      ...invoice,
      serviceName: 'Hóa đơn khác',
      billingPeriod: invoice.billingPeriod,
      currentAmount: getInvoiceListDisplayAmount(invoice),
    });
    setShowAdjustServiceFeeModal(true);
  };

  const handleAdjustServiceFee = async (data: any) => {
    try {
      await serviceFeeApi.adjustServiceFee({
        invoiceId: data.invoiceId,
        adjustmentType: data.adjustmentType,
        adjustmentAmount: data.adjustmentAmount,
        notes: data.notes,
      });
      
      // Reload service fee invoices to show the updated data
      await loadServiceFeeInvoices();
      
      // If it's a partial prepayment, also reload payment history to show the new payment record
      if (data.adjustmentType === 'PARTIAL_PREPAYMENT') {
        await loadPaymentHistory();
        alert('Điều chỉnh hóa đơn thành công! Bản ghi thanh toán trước đã được tạo trong tab "Lịch sử thanh toán".');
      } else {
        alert('Điều chỉnh hóa đơn thành công!');
      }
    } catch (error: any) {
      console.error('Error adjusting service fee:', error);
      throw new Error(error.response?.data?.message || 'Có lỗi xảy ra khi điều chỉnh hóa đơn');
    }
  };

  // Fee Categories Tab
  const FeeCategoriesTab = () => {
    // State for editing quantities and quota limits
    const [editingQuantities, setEditingQuantities] = useState<Record<string, boolean>>({});
    const [editingQuotaLimits, setEditingQuotaLimits] = useState<Record<string, boolean>>({});
    const [tempQuantities, setTempQuantities] = useState<Record<string, number>>({});
    const [tempQuotaLimits, setTempQuotaLimits] = useState<Record<string, number>>({});

    // Helper functions for fee categories
    const getPricingTypeDisplay = (feeCategory: ApartmentFeeCategory) => {
      const { feeCategory: fee } = feeCategory;
      switch (fee.pricingType) {
        case 'SINGLE_TIER':
          return 'Đơn giá cố định';
        case 'TIERED':
          return 'Đơn giá bậc thang';
        case 'QUOTA_BASED':
          return 'Đơn giá theo định mức';
        default:
          return 'Không xác định';
      }
    };

    const getPriceDisplay = (feeCategory: ApartmentFeeCategory, isGroupParent = false) => {
      if (isGroupParent) {
        return '—';
      }
      const { feeCategory: fee } = feeCategory;
      if (fee.pricingType === 'SINGLE_TIER') {
        return fee.basePrice ? `${fee.basePrice.toLocaleString('vi-VN')} VND` : 'Chưa có giá';
      } else if (fee.pricingType === 'TIERED') {
        return 'Bậc thang';
      } else if (fee.pricingType === 'QUOTA_BASED') {
        return fee.quotaPrice ? `${fee.quotaPrice.toLocaleString('vi-VN')} VND` : 'Chưa có giá';
      }
      return '--';
    };

    const getUnitDisplay = (feeCategory: ApartmentFeeCategory) => {
      return getFeeCategoryDisplayUnit(feeCategory.feeCategory);
    };

    const getQuantityEditable = (feeCategory: ApartmentFeeCategory) => {
      return isApartmentQuantityEditable(feeCategory.feeCategory);
    };

    const getQuotaLimitEditable = (feeCategory: ApartmentFeeCategory) => {
      const { feeCategory: fee } = feeCategory;
      // Allow editing quota limit for QUOTA_BASED fees
      return fee.pricingType === 'QUOTA_BASED';
    };

    const getQuantityDisplay = (feeCategory: ApartmentFeeCategory, isGroupParent = false) => {
      if (isGroupParent) {
        return '—';
      }
      const { feeCategory: fee } = feeCategory;

      if (usesPerInvoiceQuantity(fee)) {
        return 'Theo hóa đơn';
      }

      if (fee.quantitySource === 'APARTMENT_AREA') {
        const qty = feeCategory.effectiveQuantity ?? feeCategory.quantity ?? 0;
        return `${qty} m²`;
      }

      if (fee.quantitySource === 'FIXED_ONE') {
        const unit = fee.quantityUnit ? ` ${fee.quantityUnit}` : '';
        return `1${unit}`;
      }

      if (fee.autoCloseMonthly) {
        const qty = feeCategory.effectiveQuantity ?? feeCategory.quantity ?? 1;
        const unit = fee.quantityUnit ? ` ${fee.quantityUnit}` : '';
        return `${qty}${unit}`;
      }
      return 'Không áp dụng';
    };

    const getQuotaLimitDisplay = (feeCategory: ApartmentFeeCategory) => {
      const { feeCategory: fee } = feeCategory;

      if (fee.pricingType === 'QUOTA_BASED') {
        // For quota-based fees, display apartment-specific quotaLimit if set,
        // otherwise display building-level quotaLimit. Default to 0 if neither.
        return feeCategory.quotaLimit !== undefined && feeCategory.quotaLimit !== null
               ? feeCategory.quotaLimit
               : (fee.quotaLimit !== undefined && fee.quotaLimit !== null ? fee.quotaLimit : 0);
      }
      return 'Không áp dụng';
    };

    const getAutoCloseDisplay = (feeCategory: ApartmentFeeCategory) => {
      const { feeCategory: fee } = feeCategory;
      if (fee.autoCloseMonthly) {
        return 'Tự động';
      } else {
        return 'Thủ công';
      }
    };

      const handleEditFeeCategory = (feeCategory: ApartmentFeeCategory) => {
    setSelectedFeeCategory(feeCategory);
    setShowEditFeeCategoryModal(true);
  };

    const handleQuantityChange = async (feeCategory: ApartmentFeeCategory, newValue: number) => {
      try {
        const updateData = { 
          quantity: newValue,
          notes: feeCategory.notes 
        };

        await apartmentsApi.updateApartmentFeeCategory(
          id!, 
          feeCategory.feeCategoryId, 
          updateData
        );
        
        // Reload fee categories to show updated data
        await loadFeeCategories();
        
        // Clear editing state
        setEditingQuantities(prev => ({ ...prev, [feeCategory.feeCategoryId]: false }));
        
        alert('Đã cập nhật số lượng thành công!');
      } catch (error) {
        console.error('Error updating quantity:', error);
        alert('Có lỗi xảy ra khi cập nhật số lượng');
      }
    };

    const handleQuotaLimitChange = async (feeCategory: ApartmentFeeCategory, newValue: number) => {
      try {
        const updateData = { 
          quantity: feeCategory.quantity || 0, // Keep existing quantity
          quotaLimit: newValue,
          notes: feeCategory.notes 
        };

        await apartmentsApi.updateApartmentFeeCategory(
          id!, 
          feeCategory.feeCategoryId, 
          updateData
        );
        
        // Reload fee categories to show updated data
        await loadFeeCategories();
        
        // Clear editing state
        setEditingQuotaLimits(prev => ({ ...prev, [feeCategory.feeCategoryId]: false }));
        
        alert('Đã cập nhật định mức thành công!');
      } catch (error) {
        console.error('Error updating quota limit:', error);
        alert('Có lỗi xảy ra khi cập nhật định mức');
      }
    };

    const startEditingQuantity = (feeCategory: ApartmentFeeCategory) => {
      setEditingQuantities(prev => ({ ...prev, [feeCategory.feeCategoryId]: true }));
      
      // Always use quantity field for quantity editing
      const currentValue = feeCategory.quantity || 0;
      
      setTempQuantities(prev => ({ ...prev, [feeCategory.feeCategoryId]: currentValue }));
    };

    const startEditingQuotaLimit = (feeCategory: ApartmentFeeCategory) => {
      setEditingQuotaLimits(prev => ({ ...prev, [feeCategory.feeCategoryId]: true }));
      const { feeCategory: fee } = feeCategory;
      
      // Use apartment-specific quotaLimit or fallback to building level
      const currentValue = feeCategory.quotaLimit ?? fee.quotaLimit ?? 0;
      setTempQuotaLimits(prev => ({ ...prev, [feeCategory.feeCategoryId]: currentValue }));
    };

    const cancelEditingQuantity = (feeCategory: ApartmentFeeCategory) => {
      setEditingQuantities(prev => ({ ...prev, [feeCategory.feeCategoryId]: false }));
    };

    const cancelEditingQuotaLimit = (feeCategory: ApartmentFeeCategory) => {
      setEditingQuotaLimits(prev => ({ ...prev, [feeCategory.feeCategoryId]: false }));
    };

    const saveQuantityChange = async (feeCategory: ApartmentFeeCategory) => {
      const newValue = tempQuantities[feeCategory.feeCategoryId] || 0;
      await handleQuantityChange(feeCategory, newValue);
    };

    const saveQuotaLimitChange = async (feeCategory: ApartmentFeeCategory) => {
      const newValue = tempQuotaLimits[feeCategory.feeCategoryId] || 0;
      await handleQuotaLimitChange(feeCategory, newValue);
  };

  // Status management functions
  const handleStatusChange = async (feeCategory: ApartmentFeeCategory, newStatus: string) => {
    try {
      const isActive = newStatus === 'Đang áp dụng';
      console.log('Changing status for', feeCategory.feeCategory.name, 'to', newStatus);
      
      await feeCategoriesApi.updateStatus(feeCategory.feeCategoryId, isActive);
      
      // Reload fee categories and service fee invoices after status change
      await loadFeeCategories();
      await loadServiceFeeInvoices();
      
      alert(`Đã cập nhật trạng thái thành "${newStatus}"`);
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Có lỗi xảy ra khi cập nhật trạng thái');
    }
  };

  const getStatusDisplay = (isActive: boolean) => {
    if (isActive) {
      return 'Đang áp dụng';
    } else {
        return 'Không áp dụng';
    }
  };

  const getStatusColor = (isActive: boolean) => {
    if (isActive) {
      return 'bg-green-100 text-green-800';
    } else {
      return 'bg-red-100 text-red-800';
    }
  };

  const getActionButtons = (feeCategory: ApartmentFeeCategory) => {
    const buttons = [];
      const { feeCategory: fee } = feeCategory;
      
      // Show edit button for specific fee categories:
      // 1. SINGLE_TIER and autoCloseMonthly (Phí dịch vụ, Phí gửi xe ô tô)
      // 2. Or any fee that is currently editable
      const shouldShowEditButton = (fee.pricingType === 'SINGLE_TIER' && fee.autoCloseMonthly) ||
                                  fee.name.toLowerCase().includes('dịch vụ') ||
                                  fee.name.toLowerCase().includes('xe');
      
      if (shouldShowEditButton) {
    buttons.push(
      <button
        key="edit"
        onClick={() => handleEditFeeCategory(feeCategory)}
        className="text-blue-600 hover:text-blue-800 text-sm font-medium mr-2"
        title="Chỉnh sửa"
      >
        Edit
      </button>
    );
      }

    // Status-specific buttons
    if (feeCategory.feeCategory.isActive) {
      // Lock button for active categories
      buttons.push(
        <button
          key="lock"
          onClick={() => handleStatusChange(feeCategory, 'Không áp dụng')}
          className="text-orange-600 hover:text-orange-800 text-sm font-medium mr-2"
          title="Khóa phí dịch vụ"
        >
          Lock
        </button>
      );
    } else {
      // Activate button for inactive categories
      buttons.push(
        <button
          key="activate"
          onClick={() => handleStatusChange(feeCategory, 'Đang áp dụng')}
          className="text-green-600 hover:text-green-800 text-sm font-medium mr-2"
          title="Kích hoạt phí dịch vụ"
        >
          Activate
        </button>
      );
    }

    return buttons;
  };

    if (loadingFeeCategories) {
      return (
        <div className="bg-white rounded-lg border border-gray-200 p-8">
          <div className="flex justify-center items-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-2 text-gray-600">Đang tải danh mục phí...</span>
          </div>
        </div>
      );
    }

    const residentFeeCategories = filterApartmentDanhMucPhiCategories(
      filterManagementFeeCategoriesForApartmentUsage(
        filterKtdvFeeCategoriesForApartmentUsage(
          feeCategories,
          apartmentUsageTypeForFees?.code,
        ),
        apartmentUsageTypeForFees,
        buildingManagementFeeCodes,
      ),
    );
    const apartmentFeeCategoryRows = buildApartmentFeeCategoryTableRows(residentFeeCategories);

    return (
      <div className="bg-white rounded-lg border border-gray-200">
        {/* Header with refresh button */}
        <div className="px-4 py-3 border-b border-gray-200 flex justify-between items-center">
          <h3 className="text-lg font-medium text-gray-900">Danh mục phí</h3>
          <button
            onClick={loadFeeCategories}
            disabled={loadingFeeCategories}
            className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            <svg className={`w-4 h-4 mr-2 ${loadingFeeCategories ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            {loadingFeeCategories ? 'Đang tải...' : 'Làm mới'}
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white border border-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">STT</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tên phí</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Loại phí</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Đơn giá</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Số lượng</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Định mức</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Chốt phí</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Trạng thái</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ghi chú</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Thao tác</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {apartmentFeeCategoryRows.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-4 py-8 text-center text-gray-500">
                    Chưa có danh mục phí. Vui lòng tạo danh mục phí mặc định cho tòa nhà.
                  </td>
                </tr>
              ) : (
              apartmentFeeCategoryRows.map(({ item: feeCategory, isChild, isGroupParent }, index) => {
                const isEditingQuantity = editingQuantities[feeCategory.feeCategoryId];
                const isEditingQuotaLimit = editingQuotaLimits[feeCategory.feeCategoryId];
                const tempQuantity = tempQuantities[feeCategory.feeCategoryId];
                const tempQuotaLimit = tempQuotaLimits[feeCategory.feeCategoryId];
                
                return (
                <tr
                  key={feeCategory.feeCategoryId}
                  className={`hover:bg-gray-50 ${isGroupParent ? 'bg-blue-50' : ''} ${isChild ? 'bg-gray-50/60' : ''}`}
                >
                  <td className="px-4 py-3 text-sm">{index + 1}</td>
                  <td className="px-4 py-3 text-sm">
                    <button
                      onClick={() => handleEditFeeCategory(feeCategory)}
                      className={`text-left font-medium ${isGroupParent ? 'text-blue-700' : 'text-blue-600 hover:text-blue-800'} ${isChild ? 'pl-4' : ''}`}
                    >
                      {getBillServiceNameLines(feeCategory.feeCategory).map((line: string, lineIndex: number) => (
                        <div key={lineIndex}>{line}</div>
                      ))}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-sm">
                      {isGroupParent ? 'Nhóm phí' : getPricingTypeDisplay(feeCategory)}
                  </td>
                  <td className="px-4 py-3 text-sm">
                      <div>
                        <div className="font-medium">{getPriceDisplay(feeCategory, isGroupParent)}</div>
                        {!isGroupParent && (
                          <div className="text-xs text-gray-500">{getUnitDisplay(feeCategory)}</div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {isGroupParent ? (
                        <span>—</span>
                      ) : isEditingQuantity ? (
                        <div className="flex items-center space-x-2">
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={tempQuantity}
                            onChange={(e) => setTempQuantities(prev => ({
                              ...prev,
                              [feeCategory.feeCategoryId]: parseFloat(e.target.value) || 0
                            }))}
                            className="w-20 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                          <button
                            onClick={() => saveQuantityChange(feeCategory)}
                            className="text-green-600 hover:text-green-800 text-xs"
                            title="Lưu"
                          >
                            ✓
                          </button>
                          <button
                            onClick={() => cancelEditingQuantity(feeCategory)}
                            className="text-red-600 hover:text-red-800 text-xs"
                            title="Hủy"
                          >
                            ✗
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center space-x-2">
                          <span>{getQuantityDisplay(feeCategory, isGroupParent)}</span>
                          {getQuantityEditable(feeCategory) && (
                            <button
                              onClick={() => startEditingQuantity(feeCategory)}
                              className="text-blue-600 hover:text-blue-800 text-xs"
                              title="Chỉnh sửa"
                            >
                              ✏️
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {isGroupParent ? (
                        <span>—</span>
                      ) : isEditingQuotaLimit ? (
                        <div className="flex items-center space-x-2">
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={tempQuotaLimit}
                            onChange={(e) => setTempQuotaLimits(prev => ({
                              ...prev,
                              [feeCategory.feeCategoryId]: parseFloat(e.target.value) || 0
                            }))}
                            className="w-20 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                          <button
                            onClick={() => saveQuotaLimitChange(feeCategory)}
                            className="text-green-600 hover:text-green-800 text-xs"
                            title="Lưu"
                          >
                            ✓
                          </button>
                          <button
                            onClick={() => cancelEditingQuotaLimit(feeCategory)}
                            className="text-red-600 hover:text-red-800 text-xs"
                            title="Hủy"
                          >
                            ✗
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center space-x-2">
                          <span>{getQuotaLimitDisplay(feeCategory)}</span>
                          {getQuotaLimitEditable(feeCategory) && (
                            <button
                              onClick={() => startEditingQuotaLimit(feeCategory)}
                              className="text-blue-600 hover:text-blue-800 text-xs"
                              title="Chỉnh sửa định mức"
                            >
                              ✏️
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {isGroupParent ? '—' : getAutoCloseDisplay(feeCategory)}
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => {
                        const newStatus = feeCategory.feeCategory.isActive ? 'Không áp dụng' : 'Đang áp dụng';
                        handleStatusChange(feeCategory, newStatus);
                      }}
                      className={`inline-flex px-2 py-1 text-xs rounded-full cursor-pointer hover:opacity-80 transition-opacity ${getStatusColor(feeCategory.feeCategory.isActive)}`}
                      title="Click để thay đổi trạng thái"
                    >
                      {getStatusDisplay(feeCategory.feeCategory.isActive)}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {feeCategory.notes || '--'}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <div className="flex items-center">
                      {!isGroupParent && getActionButtons(feeCategory)}
                    </div>
                  </td>
                </tr>
                );
              })
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // Payment History Tab  
  const PaymentHistoryTab = () => {
    const formatPrice = (price: number) => {
      return new Intl.NumberFormat('vi-VN').format(price);
    };

    const getStatusDisplay = (status: string) => {
      switch (status) {
        case 'PAID':
          return { text: 'Đã thanh toán', class: 'bg-green-100 text-green-800' };
        case 'PENDING':
          return { text: 'Chờ thanh toán', class: 'bg-yellow-100 text-yellow-800' };
        case 'OVERDUE':
          return { text: 'Quá hạn', class: 'bg-red-100 text-red-800' };
        case 'CANCELLED':
          return { text: 'Đã hủy', class: 'bg-gray-100 text-gray-800' };
        default:
          return { text: 'Không xác định', class: 'bg-gray-100 text-gray-800' };
      }
    };

    const getPaymentMethodText = (method: string) => {
      const methods = {
        CASH: 'Tiền mặt',
        BANK_TRANSFER: 'Chuyển khoản',
        CREDIT_CARD: 'Thẻ tín dụng',
        DEBIT_CARD: 'Thẻ ghi nợ',
        ONLINE_PAYMENT: 'Thanh toán online',
        CHECK: 'Séc',
        OTHER: 'Khác'
      };
      return methods[method as keyof typeof methods] || method;
    };

    const handleBillClick = (bill: Bill) => {
      setSelectedBillId(bill.billId || bill.id);
      setShowBillDetailsModal(true);
    };

    const handleDateFilter = () => {
      loadPaymentHistory(paymentFromDate, paymentToDate);
    };

    const clearDateFilter = () => {
      const defaults = getDefaultDates();
      setPaymentFromDate(defaults.fromDate);
      setPaymentToDate(defaults.toDate);
      loadPaymentHistory(defaults.fromDate, defaults.toDate);
    };

    if (loadingPaymentHistory) {
      return (
        <div className="bg-white rounded-lg border border-gray-200 p-8">
          <div className="flex justify-center items-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-2 text-gray-600">Đang tải lịch sử thanh toán...</span>
          </div>
        </div>
      );
    }

    return (
      <div className="bg-white rounded-lg border border-gray-200">
        {/* Header with Date Filter */}
        <div className="px-4 py-3 border-b border-gray-200">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">Lịch sử thanh toán</h3>
            <button
              onClick={() => loadPaymentHistory()}
              disabled={loadingPaymentHistory}
              className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              <svg className={`w-4 h-4 mr-2 ${loadingPaymentHistory ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Làm mới
            </button>
          </div>
          
          {/* Date Filter */}
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <label className="text-sm font-medium text-gray-700">Từ ngày:</label>
              <DatePicker
                selected={paymentFromDate}
                onChange={(date) => setPaymentFromDate(date!)}
                dateFormat="MM/dd/yyyy"
                placeholderText="mm/dd/yyyy"
                className="border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-32 font-mono"
                showPopperArrow={false}
                autoComplete="off"
              />
            </div>
            <div className="flex items-center space-x-2">
              <label className="text-sm font-medium text-gray-700">đến ngày:</label>
              <DatePicker
                selected={paymentToDate}
                onChange={(date) => setPaymentToDate(date!)}
                dateFormat="MM/dd/yyyy"
                placeholderText="mm/dd/yyyy"
                className="border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-32 font-mono"
                showPopperArrow={false}
                autoComplete="off"
              />
            </div>
            <button
              onClick={handleDateFilter}
              className="inline-flex items-center px-3 py-1 bg-red-500 text-white text-sm rounded-md hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              Tìm kiếm
            </button>
            <button
              onClick={clearDateFilter}
              className="inline-flex items-center px-3 py-1 bg-gray-500 text-white text-sm rounded-md hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500"
            >
              Xóa bộ lọc
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">STT</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Mã hóa đơn</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Thời gian</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Số tiền</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Hình thức</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Nội dung</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Trạng thái</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {paymentHistory.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                    Không có lịch sử thanh toán.
                  </td>
                </tr>
              ) : (
                paymentHistory
                  .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                  .map((bill, index) => {
                    const statusInfo = getStatusDisplay(bill.status);
                    return (
                      <tr key={bill.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm">{index + 1}</td>
                        <td className="px-4 py-3 text-sm">
                          <button
                            onClick={() => handleBillClick(bill)}
                            className="font-mono text-blue-600 hover:text-blue-800 hover:underline focus:outline-none focus:underline"
                            title="Xem chi tiết hóa đơn"
                          >
                            {bill.billId || bill.id}
                          </button>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {new Date(bill.createdAt).toLocaleString('vi-VN')}
                        </td>
                        <td className="px-4 py-3 text-sm font-medium">
                          {formatPrice(
                            bill.status === 'PAID'
                              ? (bill.amountReceived ?? bill.amount)
                              : bill.amount,
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {getPaymentMethodText(bill.paymentMethod)}
                        </td>
                        <td className="px-4 py-3 text-sm max-w-xs truncate" title={bill.description}>
                          {bill.description || '--'}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex px-2 py-1 text-xs rounded-full ${statusInfo.class}`}>
                            {statusInfo.text}
                          </span>
                        </td>
                      </tr>
                    );
                  })
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // Apartment action history tab (invoices, apartment updates, fee categories)
  const ActionHistoryTab = () => {
    const [actionLogs, setActionLogs] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [historyPage, setHistoryPage] = useState(1);
    const [historyTotal, setHistoryTotal] = useState(0);
    const [historyTotalPages, setHistoryTotalPages] = useState(1);

    const loadActionLogs = useCallback(async () => {
      if (!apartment?.id) return;

      try {
        setLoading(true);
        const result = await apartmentsApi.getApartmentActionLogs(apartment.id, {
          page: historyPage,
          limit: HISTORY_PAGE_SIZE,
        });
        setActionLogs(result.data);
        setHistoryTotal(result.meta.total);
        setHistoryTotalPages(result.meta.totalPages);
      } catch (error) {
        console.error('Failed to load apartment action logs:', error);
      } finally {
        setLoading(false);
      }
    }, [apartment?.id, historyPage]);

    useEffect(() => {
      setHistoryPage(1);
    }, [apartment?.id]);

    useEffect(() => {
      if (apartment?.id) {
        loadActionLogs();
      }
    }, [apartment?.id, loadActionLogs]);

    const formatPrice = (price: number | null | undefined) => {
      if (price == null) return '--';
      return new Intl.NumberFormat('vi-VN', { maximumFractionDigits: 0 }).format(Math.round(price));
    };

    const getActionBadgeClass = (log: { domain?: string; action?: string }) => {
      if (log.domain === 'APARTMENT' || log.domain === 'FEE_CATEGORY') {
        return 'bg-blue-100 text-blue-800';
      }
      switch (log.action) {
        case 'CREATE':
          return 'bg-green-100 text-green-800';
        case 'DELETE':
          return 'bg-red-100 text-red-800';
        case 'ADJUST':
          return 'bg-orange-100 text-orange-800';
        default:
          return 'bg-gray-100 text-gray-800';
      }
    };

    const getContentLines = (log: any): string[] => {
      if (log.domain === 'INVOICE') {
        return log.detail ? [log.detail] : [];
      }

      if (Array.isArray(log.metadata?.changes) && log.metadata.changes.length > 0) {
        return log.metadata.changes.map(
          (change: { label?: string; oldValue?: string; newValue?: string }) =>
            `${change.label ?? 'Trường'}: ${change.oldValue ?? '(trống)'} → ${change.newValue ?? '(trống)'}`,
        );
      }

      if (log.detail) {
        return log.detail.split('; ').filter(Boolean);
      }

      return [];
    };

    const getNotesText = (log: any) => {
      if (log.domain !== 'INVOICE') {
        return '--';
      }

      return (
        log.notes ||
        (log.accessCard
          ? [
              `Số thẻ: ${log.accessCard.cardNumber}`,
              log.accessCard.cardCode ? `UID: ${log.accessCard.cardCode}` : null,
            ]
              .filter(Boolean)
              .join(', ')
          : '--')
      );
    };

    return (
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="px-4 py-3 border-b border-gray-200 flex justify-between items-center">
          <h3 className="text-lg font-medium text-gray-900">Lịch sử thao tác</h3>
          <button
            onClick={loadActionLogs}
            disabled={loading}
            className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            <svg className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            {loading ? 'Đang tải...' : 'Làm mới'}
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full table-fixed w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="w-12 px-3 py-3 text-left text-sm font-medium text-gray-700">STT</th>
                <th className="w-36 px-3 py-3 text-left text-sm font-medium text-gray-700">Thời gian</th>
                <th className="w-40 px-3 py-3 text-left text-sm font-medium text-gray-700">Thao tác</th>
                <th className="w-[28%] px-3 py-3 text-left text-sm font-medium text-gray-700">Nội dung</th>
                <th className="w-24 px-3 py-3 text-left text-sm font-medium text-gray-700">Kỳ HĐ</th>
                <th className="w-16 px-3 py-3 text-left text-sm font-medium text-gray-700">SL</th>
                <th className="w-24 px-3 py-3 text-left text-sm font-medium text-gray-700">Số tiền</th>
                <th className="w-[22%] px-3 py-3 text-left text-sm font-medium text-gray-700">Thẻ / Ghi chú</th>
                <th className="w-24 px-3 py-3 text-left text-sm font-medium text-gray-700">NV</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={9} className="px-4 py-8 text-center text-gray-500">
                    Đang tải lịch sử...
                  </td>
                </tr>
              ) : actionLogs.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-4 py-8 text-center text-gray-500">
                    Chưa có thao tác nào được ghi nhận.
                  </td>
                </tr>
              ) : (
                actionLogs.map((log, index) => {
                  const contentLines = getContentLines(log);
                  const rowNumber = (historyPage - 1) * HISTORY_PAGE_SIZE + index + 1;

                  return (
                  <tr key={log.id} className="hover:bg-gray-50 align-top">
                    <td className="px-3 py-3 text-sm">{rowNumber}</td>
                    <td className="px-3 py-3 text-sm whitespace-nowrap">
                      {log.createdAt
                        ? new Date(log.createdAt).toLocaleString('vi-VN')
                        : '--'}
                    </td>
                    <td className="px-3 py-3 text-sm">
                      <span
                        className={`inline-flex max-w-full px-2 py-1 text-xs font-medium rounded-full whitespace-normal ${getActionBadgeClass(log)}`}
                      >
                        {log.summary || log.action}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-sm text-gray-700">
                      {contentLines.length > 0 ? (
                        <div className="space-y-1">
                          {contentLines.map((line, lineIndex) => (
                            <div key={lineIndex} className="break-words">{line}</div>
                          ))}
                        </div>
                      ) : (
                        '--'
                      )}
                    </td>
                    <td className="px-3 py-3 text-sm whitespace-nowrap">{log.billingPeriod || '--'}</td>
                    <td className="px-3 py-3 text-sm text-center">
                      {log.quantity != null ? log.quantity : '--'}
                    </td>
                    <td className="px-3 py-3 text-sm font-medium whitespace-nowrap">
                      {log.domain === 'INVOICE' ? formatPrice(log.amount) : '--'}
                    </td>
                    <td className="px-3 py-3 text-sm text-gray-600 break-words">
                      {getNotesText(log)}
                    </td>
                    <td className="px-3 py-3 text-sm break-words">
                      {log.creator?.name || log.creator?.email || '--'}
                    </td>
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <div className="px-4 pb-4">
          <BulkListPagination
            page={historyPage}
            totalPages={historyTotalPages}
            total={historyTotal}
            onPageChange={setHistoryPage}
            t={t}
          />
        </div>
      </div>
    );
  };

  // Notification History Tab
  const NotificationHistoryTab = () => {
    const [notifications, setNotifications] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
      if (apartment?.id) {
        loadNotificationHistory();
      }
    }, [apartment?.id]);

    const loadNotificationHistory = async () => {
      if (!apartment?.id) return;
      
      try {
        setLoading(true);
        const { emailNotificationsApi } = await import('../../services/emailNotificationsApi');
        // Use the original API to get ALL notification types
        const data = await emailNotificationsApi.getApartmentNotificationHistory(apartment!.id);
        setNotifications(data);
      } catch (error) {
        console.error('Failed to load notification history:', error);
      } finally {
        setLoading(false);
      }
    };

    const getNotificationTypeText = (type: string) => {
      switch (type) {
        case 'SERVICE_FEE_REMINDER':
          return 'Thông báo phí';
        case 'PAYMENT_CONFIRMATION':
          return 'Xác nhận thanh toán';
        case 'OVERDUE_NOTICE':
          return 'Thông báo quá hạn';
        case 'GENERAL_ANNOUNCEMENT':
          return 'Thông báo chung';
        default:
          return type;
      }
    };

    const getDeliveryMethodText = (notification: any) => {
      const methods: string[] = notification.deliveryMethods || ['EMAIL'];
      const labels: string[] = [];
      if (methods.includes('EMAIL')) labels.push('Email');
      if (methods.includes('APP')) labels.push('App Push');
      return labels.length > 0 ? labels.join(', ') : 'Email';
    };

    const getStatusInfo = (status: string) => {
      switch (status) {
        case 'SENT':
          return { text: 'Đã gửi', class: 'bg-green-100 text-green-800' };
        case 'PENDING':
          return { text: 'Đang chờ', class: 'bg-yellow-100 text-yellow-800' };
        case 'FAILED':
          return { text: 'Thất bại', class: 'bg-red-100 text-red-800' };
        case 'CANCELLED':
          return { text: 'Đã hủy', class: 'bg-gray-100 text-gray-800' };
        default:
          return { text: status, class: 'bg-gray-100 text-gray-800' };
      }
    };

    const handleEmailClick = (notification: any) => {
      setSelectedEmailNotification(notification);
      setShowEmailDetailModal(true);
    };

    return (
      <div className="bg-white rounded-lg border border-gray-200">
        {/* Header */}
        <div className="px-4 py-3 border-b border-gray-200 flex justify-between items-center">
          <h3 className="text-lg font-medium text-gray-900">Lịch sử thông báo</h3>
          <button
            onClick={loadNotificationHistory}
            disabled={loading}
            className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            <svg className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            {loading ? 'Đang tải...' : 'Làm mới'}
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">STT</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Ngày gửi</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Loại thông báo</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Hình thức thông báo</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Tiêu đề</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Email</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Trạng thái</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                    <div className="flex justify-center items-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mr-2"></div>
                      Đang tải...
                    </div>
                  </td>
                </tr>
              ) : notifications.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                    Chưa có lịch sử thông báo
                  </td>
                </tr>
              ) : (
                notifications.map((notification, index) => {
                  const statusInfo = getStatusInfo(notification.status);
                  return (
                    <tr key={notification.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm">{index + 1}</td>
                      <td className="px-4 py-3 text-sm">
                        {notification.sentAt 
                          ? new Date(notification.sentAt).toLocaleDateString('vi-VN')
                          : new Date(notification.createdAt).toLocaleDateString('vi-VN')
                        }
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {getNotificationTypeText(notification.type)}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {getDeliveryMethodText(notification)}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <button
                          onClick={() => handleEmailClick(notification)}
                          className="text-blue-600 hover:text-blue-800 hover:underline focus:outline-none focus:underline max-w-xs truncate block"
                          title={notification.subject}
                        >
                          {notification.subject}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-sm">{notification.recipientEmail}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-1 text-xs rounded-full ${statusInfo.class}`}>
                          {statusInfo.text}
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // General Info Tab Component - FIXED VERSION
  const GeneralInfoTab = () => {
    // Local form state to prevent parent re-renders
    const [localFormData, setLocalFormData] = useState<UpdateApartmentData>(formData);
    
    // Sync local state when formData.id changes (new apartment loaded)
    useEffect(() => {
      setLocalFormData(formData);
    }, [formData.id]);

    const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
      const { name, value, type } = e.target;
      const newValue = type === 'checkbox' ? (e.target as HTMLInputElement).checked 
                     : name === 'area' ? (value ? parseFloat(value) : undefined)
                     : name === 'registeredQuotaCount' || name === 'populationCount'
                       ? (value ? parseInt(value, 10) : undefined)
                     : value;
      
      // Update local state immediately (no re-render of parent)
      setLocalFormData(prev => ({
        ...prev,
        [name]: newValue
      }));
      
      // DO NOT update parent state during typing - this causes focus loss!
      // Parent state will only be updated on form submit
    };

    const handleFormSubmit = async (e: React.FormEvent) => {
      e.preventDefault();
      setError(null);

      // Sync local state to parent state before submit
      setFormData(localFormData);

      // Validation
      if (!localFormData.code?.trim()) {
        setError(t('pleaseEnterApartmentCode'));
        return;
      }
      if (!localFormData.ownerName?.trim()) {
        setError(t('pleaseEnterOwnerName'));
        return;
      }
      if (!localFormData.buildingId) {
        setError(t('pleaseSelectBuilding'));
        return;
      }
      if (!isValidEmailList(localFormData.ownerEmail)) {
        setError(t('invalidEmailList'));
        return;
      }
      if (!isValidEmailList(localFormData.invoiceEmail)) {
        setError(t('invalidEmailList'));
        return;
      }

      try {
        setLoading(true);
        const submitData = {
          ...localFormData,
          groupId: localFormData.groupId || undefined,
          usageTypeId: localFormData.usageTypeId || undefined,
          area: localFormData.area || undefined,
          registeredQuotaCount: localFormData.registeredQuotaCount ?? undefined,
          populationCount: localFormData.populationCount ?? undefined,
          occupancyStatus: localFormData.occupancyStatus || undefined,
          handoverDate: localFormData.handoverDate?.trim()
            ? parseDisplayDateToIso(localFormData.handoverDate)
            : null,
          name: localFormData.name?.trim() || undefined,
          peCode: localFormData.peCode?.trim() || undefined,
        };
        await apartmentsApi.updateApartment(submitData);
        await loadApartment();
        alert(t('apartmentUpdatedSuccess'));
      } catch (err) {
        setError(t('errorUpdatingApartment'));
      } finally {
        setLoading(false);
      }
    };

    // Memoize filtered groups to prevent re-renders
    const currentFilteredGroups = useMemo(() => (
      localFormData.buildingId ? apartmentGroups : []
    ), [apartmentGroups, localFormData.buildingId]);

    return (
      <form onSubmit={handleFormSubmit} className="space-y-6">
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
          {error}
        </div>
      )}
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column - General Information */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <h3 className="text-lg font-medium text-gray-800 mb-4 text-blue-600 border-b border-blue-200 pb-2">
            {t('generalInfo')}
          </h3>
          
          <div className="space-y-4">
            {/* Building Selection - Disabled (cannot be changed) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Chọn chung cư
              </label>
              <select
                name="buildingId"
                  value={localFormData.buildingId}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-gray-100 text-gray-500 cursor-not-allowed"
                disabled
              >
                <option value="">-- Chọn chung cư --</option>
                  {buildingOptions}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                Chung cư không thể thay đổi sau khi căn hộ đã được tạo.
              </p>
            </div>

            {/* Apartment Group Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Chọn nhóm căn hộ
              </label>
              <select
                name="groupId"
                  value={localFormData.groupId}
                  onChange={handleFormChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">{t('noApartmentGroup')}</option>
                  {currentFilteredGroups.map((group) => (
                  <option key={group.id} value={group.id}>
                    {group.name}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="mt-2 text-sm text-red-600 hover:text-red-800 border border-red-300 px-3 py-1 rounded"
              >
                + {t('addApartmentGroup')}
              </button>
            </div>

            {/* Apartment Code */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('apartmentCode')}
              </label>
              <input
                type="text"
                name="code"
                  value={localFormData.code}
                  onChange={handleFormChange}
                placeholder={t('apartmentCodePlaceholder')}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            {/* Apartment Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('apartmentName')}
              </label>
              <input
                type="text"
                name="name"
                value={localFormData.name || ''}
                onChange={handleFormChange}
                placeholder={t('apartmentNamePlaceholder')}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Occupancy Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('apartmentOccupancyStatus')}
              </label>
              <select
                name="occupancyStatus"
                value={localFormData.occupancyStatus || ''}
                onChange={handleFormChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">--</option>
                <option value="OWNER_USING">{t('occupancyOwnerUsing')}</option>
                <option value="RENTED">{t('occupancyRented')}</option>
                <option value="FOR_RENT">{t('occupancyForRent')}</option>
                <option value="FOR_SALE">{t('occupancyForSale')}</option>
              </select>
            </div>

            {/* Handover Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('handoverDate')}
              </label>
              <input
                type="text"
                name="handoverDate"
                value={localFormData.handoverDate || ''}
                onChange={handleFormChange}
                placeholder={t('handoverDatePlaceholder')}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="mt-1 text-xs text-gray-500">{t('handoverDateEmptyHint')}</p>
            </div>

            {/* PE Code */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('peCode')}
              </label>
              <input
                type="text"
                name="peCode"
                value={localFormData.peCode || ''}
                onChange={handleFormChange}
                placeholder={t('peCodePlaceholder')}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Population Count */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('populationCount')}
              </label>
              <input
                type="number"
                name="populationCount"
                value={localFormData.populationCount ?? ''}
                onChange={handleFormChange}
                placeholder={t('populationCountPlaceholder')}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                min="0"
                step="1"
              />
            </div>

            {/* Usage Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('usageType')}
              </label>
              <select
                name="usageTypeId"
                value={localFormData.usageTypeId || ''}
                onChange={handleFormChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">{t('noUsageType')}</option>
                {usageTypes.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.name} ({type.code})
                  </option>
                ))}
              </select>
            </div>

            {/* Area */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('apartmentArea')}
              </label>
              <input
                type="number"
                name="area"
                  value={localFormData.area || ''}
                  onChange={handleFormChange}
                placeholder={t('apartmentAreaPlaceholder')}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                min="0"
                step="0.01"
              />
            </div>
          </div>
        </div>

        {/* Right Column - Owner Information */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <h3 className="text-lg font-medium text-gray-800 mb-4 text-blue-600 border-b border-blue-200 pb-2">
            Thông tin chủ/đại diện căn hộ
          </h3>
          
          <div className="space-y-4">
            {/* Owner Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tên chủ hộ
              </label>
              <input
                type="text"
                name="ownerName"
                  value={localFormData.ownerName}
                  onChange={handleFormChange}
                placeholder="Tên chủ căn hộ"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

              {/* Owner Phone */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                  Số điện thoại
              </label>
              <input
                type="tel"
                name="ownerPhone"
                  value={localFormData.ownerPhone}
                  onChange={handleFormChange}
                  placeholder="Số điện thoại"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

              {/* Owner Email - Fee notification */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('feeNotificationEmail')}
              </label>
              <input
                type="text"
                name="ownerEmail"
                  value={localFormData.ownerEmail}
                  onChange={handleFormChange}
                  placeholder={t('feeNotificationEmailPlaceholder')}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="mt-1 text-xs text-gray-500">{t('multipleEmailsHint')}</p>
            </div>

              {/* Invoice Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('invoiceEmail')}
              </label>
              <input
                type="text"
                name="invoiceEmail"
                  value={localFormData.invoiceEmail}
                  onChange={handleFormChange}
                  placeholder={t('invoiceEmailPlaceholder')}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="mt-1 text-xs text-gray-500">{t('multipleEmailsHint')}</p>
            </div>

            {/* Tax Code */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Mã số thuế
              </label>
              <input
                type="text"
                name="taxCode"
                  value={localFormData.taxCode}
                  onChange={handleFormChange}
                placeholder="Mã số thuế"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

              {/* Address */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                  Địa chỉ
              </label>
              <textarea
                  name="address"
                  value={localFormData.address}
                  onChange={handleFormChange}
                  placeholder="Địa chỉ chi tiết"
                rows={3}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

              {/* VAT Invoice */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="hasVatInvoice"
                  checked={localFormData.hasVatInvoice}
                  onChange={handleFormChange}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label className="ml-2 block text-sm text-gray-700">
                  Có xuất hóa đơn VAT
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Notes */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <h3 className="text-lg font-medium text-gray-800 mb-4 text-blue-600 border-b border-blue-200 pb-2">
            Ghi chú
          </h3>
          <textarea
            name="notes"
            value={localFormData.notes}
            onChange={handleFormChange}
            placeholder="Ghi chú về căn hộ..."
            rows={4}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
      </div>

      {/* Submit Button */}
        <div className="flex justify-end space-x-4">
        <button
          type="submit"
          disabled={loading}
            className="bg-blue-500 hover:bg-blue-600 disabled:bg-gray-400 text-white px-6 py-2 rounded-lg transition-colors flex items-center space-x-2"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Đang lưu...</span>
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
                <span>Lưu thay đổi</span>
              </>
            )}
        </button>
      </div>
    </form>
    );
  };

  const tabs = [
    {
      id: 'general',
      label: 'Thông tin chung'
    },
    {
      id: 'serviceFees',
      label: 'Phí dịch vụ'
    },
    {
      id: 'members',
      label: 'Thành viên'
    },
    {
      id: 'feeCategories',
      label: 'Danh mục phí'
    },
    {
      id: 'paymentHistory',
      label: 'Lịch sử thanh toán'
    },
    {
      id: 'invoiceActionHistory',
      label: 'Lịch sử thao tác'
    },
    {
      id: 'notificationHistory',
      label: 'Lịch sử thông báo'
    }
  ];



  if (loadingData) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-600">{t('loadingApartments')}</div>
      </div>
    );
  }

  if (!apartment) {
    return (
      <div className="text-center py-12">
        <div className="text-red-500 text-lg mb-4">Apartment not found</div>
        <Link
          to="/apartments"
          className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-lg transition-colors"
        >
          {t('backToList')}
        </Link>
      </div>
    );
  }

  const handleOpenChat = async () => {
    if (!id) return;
    
    try {
      const room = await chatApi.getOrCreateRoomForApartment(id);
      navigate(`/chat/${room.id}`);
    } catch (error) {
      console.error('Error opening chat:', error);
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">
            Chi tiết căn hộ: {apartment.code}
          </h1>
          <p className="text-gray-600">{apartment.ownerName}</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={handleOpenChat}
            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-colors flex items-center space-x-2"
          >
            <ChatBubbleLeftIcon className="h-5 w-5" />
            <span>Tin nhắn</span>
          </button>
          <Link
            to="/apartments"
            className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg transition-colors"
          >
            Quay lại danh sách
          </Link>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="mb-6">
        {activeTab === 'general' && (
          <GeneralInfoTab />
        )}
        {activeTab === 'serviceFees' && <ServiceFeesTab />}
        {activeTab === 'members' && <MembersTab />}
        {activeTab === 'feeCategories' && <FeeCategoriesTab />}
        {activeTab === 'paymentHistory' && <PaymentHistoryTab />}
        {activeTab === 'invoiceActionHistory' && <ActionHistoryTab />}
        {activeTab === 'notificationHistory' && <NotificationHistoryTab />}
      </div>

      {/* Add Member Modal */}
      <AddMemberModal 
        isOpen={showAddMemberModal} 
        onClose={handleModalClose}
        onSuccess={loadMembers}
        apartmentId={id || ''}
        existingMembers={members}
      />

      {/* Edit Member Modal */}
      <EditMemberModal 
        isOpen={showEditMemberModal} 
        onClose={() => {
          setShowEditMemberModal(false);
          setSelectedMember(null);
        }}
        onSuccess={loadMembers}
        member={selectedMember}
      />

      {/* Edit Fee Category Modal */}
      <EditFeeCategoryModal 
        isOpen={showEditFeeCategoryModal} 
        onClose={() => {
          setShowEditFeeCategoryModal(false);
          setSelectedFeeCategory(null);
        }}
        onSave={handleSaveFeeCategory}
        feeCategory={selectedFeeCategory}
      />

      {/* Add Fixed Fee Modal */}
      <AddFixedServiceFeeModal 
        isOpen={showAddFixedFeeModal} 
        onClose={() => {
          setShowAddFixedFeeModal(false);
          setSelectedServiceFeeType('');
          setSelectedFeeForModal(null);
        }}
        serviceType={selectedServiceFeeType}
        feeCategory={selectedFeeForModal}
        apartmentId={id || ''}
        buildingId={apartment?.buildingId}
        onSave={handleAddServiceFee}
      />

      {/* Add Usage Fee Modal */}
      <AddUsageServiceFeeModal 
        isOpen={showAddUsageFeeModal} 
        onClose={() => {
          setShowAddUsageFeeModal(false);
          setSelectedServiceFeeType('');
          setSelectedFeeForModal(null);
        }}
        serviceType={selectedServiceFeeType}
        feeCategory={selectedFeeForModal}
        apartmentId={id || ''}
        buildingId={apartment?.buildingId}
        onSave={handleAddServiceFee}
      />

      {/* Add Other Invoice Modal */}
      <AddOtherServiceFeeModal 
        isOpen={showAddOtherInvoiceModal} 
        onClose={() => {
          setShowAddOtherInvoiceModal(false);
        }}
        apartmentId={id}
        onSave={handleAddServiceFee}
      />

      <AddCasualParkingFeeModal
        isOpen={showCasualParkingModal}
        onClose={() => setShowCasualParkingModal(false)}
        apartmentId={id || ''}
        buildingId={apartment?.buildingId || ''}
        onSuccess={() => {
          loadServiceFeeInvoices();
        }}
      />

      {/* Adjust Service Fee Modal */}
      <AdjustServiceFeeModal 
        isOpen={showAdjustServiceFeeModal} 
        onClose={() => {
          setShowAdjustServiceFeeModal(false);
          setSelectedInvoiceForAdjustment(null);
        }}
        invoice={selectedInvoiceForAdjustment}
        onSave={handleAdjustServiceFee}
      />

      {/* Bill Detail Modal */}
      <BillDetailsModal
        isOpen={showBillDetailsModal}
        onClose={() => {
          setShowBillDetailsModal(false);
          setSelectedBillId('');
        }}
        billId={selectedBillId}
      />

      {/* Email Detail Modal */}
      <EmailDetailModal 
        isOpen={showEmailDetailModal} 
        onClose={() => {
          setShowEmailDetailModal(false);
          setSelectedEmailNotification(null);
        }}
        emailNotification={selectedEmailNotification}
      />

      <ApartmentServiceFeePaymentModal
        isOpen={showServiceFeePaymentModal}
        onClose={() => setShowServiceFeePaymentModal(false)}
        apartmentId={id || ''}
        apartmentCode={apartment.code}
        onSuccess={async () => {
          await loadServiceFeeInvoices();
          await loadPaymentHistory();
        }}
      />

      <ApartmentFeePdfExportModal
        isOpen={showServiceFeePdfModal}
        onClose={() => setShowServiceFeePdfModal(false)}
        apartmentId={id || ''}
        apartmentCode={apartment.code}
        buildingId={apartment.buildingId}
        billingPeriods={serviceFeeInvoices.map((invoice) => invoice.billingPeriod).filter(Boolean)}
      />

      <ApartmentFeeNotificationModal
        isOpen={showServiceFeeNotificationModal}
        onClose={() => setShowServiceFeeNotificationModal(false)}
        apartmentId={id || ''}
        apartmentCode={apartment.code}
        ownerEmail={apartment.ownerEmail || apartment.invoiceEmail || ''}
        members={members}
      />
    </div>
  );
};

export default ApartmentDetailsEnhanced;

